import { db, doc, getDoc, collection, getDocs, query, orderBy, startAt, endAt } from "../../firebaseClient";

// Prefix for Contract Review flows
export const CONTRACT_REVIEW_PREFIX = "CONTRACT_REVIEW_";

export interface RescuedDisplayData {
  hasLink: boolean;
  displayStatus: string;
  currentStepLabel: string;
  lastUpdatedAt: string;
  flowId: string;
}

export interface RescueCandidate {
  flowId: string;
  status: string;
  updatedAt: string;
  reason: string;
}

export interface RescueResult {
  targetId: string;
  found: boolean;
  candidates: RescueCandidate[];
}

const TERMINAL_STATUSES = [
  "APPROVED", "FULLY_APPROVED", "REJECTED", "RETURNED", 
  "LEGAL_RETURNED", "COMPLETED", "CANCELLED", "ARCHIVED"
];

/**
 * Checks if a flow status should be considered as "History/Closed"
 */
export function isFlowClosed(status: string, currentStageKey?: string | null): boolean {
    const s = (status || "").toUpperCase();
    if (TERMINAL_STATUSES.includes(s)) return true;
    if (currentStageKey === 'DONE' || s === 'DONE') return true;
    return false;
}

/**
 * Probes for an approval flow associated with a document ID.
 * Returns the flow object and data if found.
 */
export async function probeFlowForRequest(docId: string, module = "contractDraft"): Promise<{ id: string; data: any } | null> {
  const candidates = [
    `${CONTRACT_REVIEW_PREFIX}${docId}`,
    `${module}_${docId}`,
    docId
  ];

  for (const id of candidates) {
    try {
      const snap = await getDoc(doc(db, "approvalFlows", id));
      if (snap.exists) return { id: snap.id, data: snap.data() };
    } catch (e) { /* ignore */ }
  }
  return null;
}

/**
 * Derived Display Data for Badges using the requested logic
 */
export function deriveRescuedDisplay(flowId: string, flowData: any): RescuedDisplayData {
  const status = flowData?.status || 'UNKNOWN';
  const stages = flowData?.stages || [];
  
  let label = "處理中";
  if (isFlowClosed(status, flowData.currentStageKey)) {
    if (status.includes('REJECT')) label = "已駁回";
    else if (status.includes('APPROV') || status.includes('COMPLET')) label = "已核准";
    else if (status.includes('RETURN')) label = "已退回";
    else label = status;
  } else {
    // Mapping current stage label
    const active = stages.find((s: any) => s.key === flowData.currentStageKey) 
                || stages.find((s: any) => s.status === 'PENDING' || (s.status as string) === 'IN_REVIEW');
    if (active) label = active.label;
  }

  return {
    hasLink: true,
    displayStatus: status,
    currentStepLabel: label,
    lastUpdatedAt: flowData.updatedAt?.toDate?.()?.toISOString() || '',
    flowId
  };
}

/**
 * Specialized scanner for Orphan Flows (CONTRACT_REVIEW_ prefix)
 */
export async function scanOrphanContractFlows(existingRequestIds: Set<string>): Promise<any[]> {
  const orphans: any[] = [];
  try {
    const q = query(
      collection(db, "approvalFlows"), 
      orderBy("__name__"), 
      startAt(CONTRACT_REVIEW_PREFIX), 
      endAt(CONTRACT_REVIEW_PREFIX + "\uf8ff")
    );

    const snap = await getDocs(q);
    
    snap.docs.forEach(d => {
        const id = d.id;
        const reqId = id.replace(CONTRACT_REVIEW_PREFIX, "");
        if (!existingRequestIds.has(reqId)) {
            orphans.push({ id: reqId, flowId: id, ...d.data(), isOrphan: true });
        }
    });
  } catch (e) {
    console.error("[Rescue] Scoped orphan scan failed", e);
  }
  return orphans;
}

/**
 * Scans for requests that might need rescuing (Missing link but flow exists)
 */
export async function scanForRescue(): Promise<RescueResult[]> {
  const results: RescueResult[] = [];
  try {
    const reqSnap = await db.collection("approval_requests")
      .where("module", "==", "contractDraft")
      .get();
    
    // Fix: Explicitly type Set to string to avoid type inference issues when passing to scanOrphanContractFlows
    const requestIds = new Set<string>(reqSnap.docs.map(d => d.id));
    const orphans = await scanOrphanContractFlows(requestIds);

    for (const d of reqSnap.docs) {
        const req = d.data();
        const candidates: RescueCandidate[] = [];
        const res = await probeFlowForRequest(d.id, "contractDraft");
        if (res && (!req.approvalFlowId || req.approvalFlowId !== res.id)) {
            candidates.push({
                flowId: res.id,
                status: res.data.status,
                updatedAt: res.data.updatedAt?.toDate?.().toLocaleString() || "",
                reason: "Pattern Match"
            });
        }
        if (candidates.length > 0) {
            results.push({ targetId: d.id, found: true, candidates });
        }
    }
  } catch (e) {
    console.error("[Rescue] Scan failed", e);
  }
  return results;
}

export async function performRescueLink(targetId: string, candidate: RescueCandidate): Promise<void> {
  const reqRef = doc(db, "approval_requests", targetId);
  await reqRef.update({ 
    approvalFlowId: candidate.flowId,
    updatedAt: new Date().toISOString()
  });
}

export async function performRollbackToDraft(targetId: string): Promise<{ collection: string }> {
  const reqRef = doc(db, "approval_requests", targetId);
  await reqRef.update({ 
    status: 'DRAFT', 
    approvalFlowId: null,
    updatedAt: new Date().toISOString()
  });
  return { collection: "approval_requests" };
}
